<script setup lang="ts">
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import type {
  DeliveryStatus,
  JournalDeliveryData,
  JournalEventData,
  JournalLaunchData,
  LaunchEventPageData,
  LaunchJournalPageData,
  LaunchStatus,
  PublicationData,
  ScenarioOption,
} from '@/views/external-launch/types'
import { formatDateToMonthShort } from '@core/utils/formatters'
import { getDateLocale } from '@core/utils/locale'

// Журнал показывает запуски своих сценариев и исходы доставок, а секретов системы обучения в
// нём нет: роли те же, что у публикаций.
definePage({
  meta: {
    action: 'read',
    subject: 'games',
  },
})

const { t } = useI18n()
const { locale } = useI18n({ useScope: 'global' })
const route = useRoute('external-launch-journal-publication')
const router = useRouter()
const dateLocale = getDateLocale(locale.value)
const userData = useCookie<any>('userData')

const userTimeZone = computed(
  () => userData.value?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
)

const orgSettings = useCookie<any>('organizationSettings')

if (!(orgSettings.value?.enabledFeatures ?? []).includes('external-launch'))
  await router.replace('/games/list')

const publicationId = computed(() => String(route.params.publication))

const publication = ref<PublicationData | null>(null)
const scenarioTitle = ref('')

const activeTab = ref<'launches' | 'events'>('launches')

const launches = ref<JournalLaunchData[]>([])
const launchesTotal = ref(0)
const launchesPage = ref(1)
const launchesPerPage = ref(25)
const isLoadingLaunches = ref(false)

const events = ref<JournalEventData[]>([])
const eventsTotal = ref(0)
const eventsPage = ref(1)
const eventsPerPage = ref(25)
const isLoadingEvents = ref(false)

const loadFailed = ref(false)

const isSnackbarVisible = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const isRevokeConfirmVisible = ref(false)
const launchToRevoke = ref<JournalLaunchData | null>(null)

const launchHeaders = [
  { title: t('externalLaunch.manage.journal.headers.learner'), key: 'learner', sortable: false },
  { title: t('externalLaunch.manage.journal.headers.user'), key: 'userEmail', sortable: false },
  { title: t('externalLaunch.manage.journal.headers.attempt'), key: 'attemptNo', sortable: false },
  { title: t('externalLaunch.manage.journal.headers.status'), key: 'status', sortable: false },
  { title: t('externalLaunch.manage.journal.headers.game'), key: 'game', sortable: false },
  { title: t('externalLaunch.manage.journal.headers.started_at'), key: 'createdAt', sortable: false },
  { title: t('externalLaunch.manage.journal.headers.actions'), key: 'actions', sortable: false },
]

const eventHeaders = [
  { title: t('externalLaunch.manage.journal.headers.event_type'), key: 'type', sortable: false },
  { title: t('externalLaunch.manage.journal.headers.refusal'), key: 'refusalCode', sortable: false },
  { title: t('externalLaunch.manage.journal.headers.request_proof'), key: 'requestProof', sortable: false },
  { title: t('externalLaunch.manage.journal.headers.ip'), key: 'ip', sortable: false },
  { title: t('externalLaunch.manage.journal.headers.user_agent'), key: 'userAgent', sortable: false },
  { title: t('externalLaunch.manage.journal.headers.happened_at'), key: 'createdAt', sortable: false },
]

const notify = (text: string, color: string = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  isSnackbarVisible.value = true
}

const moment = (value: string | null): string =>
  value ? formatDateToMonthShort(value, false, dateLocale, userTimeZone.value) : '—'

const fetchPublication = async () => {
  try {
    publication.value = await $api<PublicationData>(`/external-launch/publications/${publicationId.value}`)
  }
  catch {
    publication.value = null
  }
}

const fetchScenarioTitle = async () => {
  if (publication.value === null)
    return

  try {
    const scenarios = (await $api<ScenarioOption[]>('/scenarios/all-for-ai')) ?? []

    scenarioTitle.value = scenarios
      .find(scenario => String(scenario.id) === String(publication.value?.scenarioId))?.title ?? ''
  }
  catch {
    scenarioTitle.value = ''
  }
}

const fetchLaunches = async () => {
  isLoadingLaunches.value = true

  try {
    const page = await $api<LaunchJournalPageData>(`/external-launch/publications/${publicationId.value}/journal`, {
      query: {
        limit: launchesPerPage.value,
        offset: (launchesPage.value - 1) * launchesPerPage.value,
      },
    })

    launches.value = page?.items ?? []
    launchesTotal.value = page?.total ?? 0
    loadFailed.value = false
  }
  catch {
    launches.value = []
    launchesTotal.value = 0
    loadFailed.value = true
  }
  finally {
    isLoadingLaunches.value = false
  }
}

const fetchEvents = async () => {
  isLoadingEvents.value = true

  try {
    const page = await $api<LaunchEventPageData>('/external-launch/journal/events', {
      query: {
        publication_id: publicationId.value,
        limit: eventsPerPage.value,
        offset: (eventsPage.value - 1) * eventsPerPage.value,
      },
    })

    events.value = page?.items ?? []
    eventsTotal.value = page?.total ?? 0
  }
  catch {
    events.value = []
    eventsTotal.value = 0
  }
  finally {
    isLoadingEvents.value = false
  }
}

await fetchPublication()
await Promise.all([fetchScenarioTitle(), fetchLaunches(), fetchEvents()])

watch([launchesPage, launchesPerPage], fetchLaunches)
watch([eventsPage, eventsPerPage], fetchEvents)

const launchStatusColor = (status: LaunchStatus): string => {
  if (status === 'result_ready')
    return 'success'
  if (status === 'revoked' || status === 'not_held')
    return 'secondary'
  if (status === 'evaluation_failed' || status === 'result_timed_out' || status === 'assignment_changed')
    return 'error'

  return 'info'
}

const deliveryStatusColor = (status: DeliveryStatus): string => {
  if (status === 'done')
    return 'success'
  if (status === 'needs_attention')
    return 'error'

  return 'info'
}

// Присланное системой обучения имя — только для показа: подтверждать личность им нельзя.
const learnerLabel = (launch: JournalLaunchData): string =>
  launch.displayName || launch.assertedEmail || launch.externalSubject || '—'

const deliveryResultLabel = (delivery: JournalDeliveryData): string =>
  delivery.result ? t(`externalLaunch.manage.journal.result.${delivery.result}`) : '—'

const deliveryScoreLabel = (delivery: JournalDeliveryData): string => {
  if (delivery.score === null)
    return '—'

  return delivery.passed === null
    ? String(delivery.score)
    : t(
      delivery.passed
        ? 'externalLaunch.manage.journal.score_passed'
        : 'externalLaunch.manage.journal.score_failed',
      { value: delivery.score },
    )
}

const externalStateLabel = (delivery: JournalDeliveryData): string =>
  delivery.externalState ? JSON.stringify(delivery.externalState) : '—'

const retryDelivery = async (delivery: JournalDeliveryData) => {
  try {
    await $api(`/external-launch/deliveries/${delivery.id}/retry`, { method: 'POST' })
    notify(t('externalLaunch.manage.journal.messages.retry_queued'))
    await fetchLaunches()
  }
  catch {
    notify(t('externalLaunch.manage.messages.error'), 'error')
  }
}

const askRevoke = (launch: JournalLaunchData) => {
  launchToRevoke.value = launch
  isRevokeConfirmVisible.value = true
}

// Один запрос на всю попытку: строк у неё может быть несколько (ученик вернулся в ту же игру
// по новой ссылке), но ручка отзывает попытку целиком — по владельцу находит все живые строки
// той же игры и гасит их вместе с токенами. Цикл по `launchIds` тут был бы не страховкой, а
// лишним риском: упади второй запрос, живой токен остался бы висеть.
const confirmRevoke = async (confirmed: boolean) => {
  const launch = launchToRevoke.value

  if (!confirmed || launch === null)
    return

  try {
    await $api(`/external-launch/launches/${launch.launchId}/revoke`, { method: 'POST' })

    notify(t('externalLaunch.manage.journal.messages.revoked'))
    await fetchLaunches()
  }
  catch {
    notify(t('externalLaunch.manage.messages.error'), 'error')
  }
  finally {
    launchToRevoke.value = null
  }
}
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center flex-wrap gap-4 mb-4">
      <div>
        <h4 class="text-h4">
          {{ t('externalLaunch.manage.journal.title') }}
        </h4>
        <div class="text-body-2 text-medium-emphasis">
          {{ scenarioTitle || t('externalLaunch.manage.journal.unknown_scenario') }}
        </div>
      </div>
      <VBtn
        variant="tonal"
        color="secondary"
        prepend-icon="tabler-arrow-left"
        :to="{ name: 'external-launch-publications' }"
      >
        {{ t('externalLaunch.manage.journal.back') }}
      </VBtn>
    </div>

    <VCard>
      <VTabs v-model="activeTab">
        <VTab value="launches">
          {{ t('externalLaunch.manage.journal.tabs.launches') }}
        </VTab>
        <VTab value="events">
          {{ t('externalLaunch.manage.journal.tabs.events') }}
        </VTab>
      </VTabs>

      <VWindow v-model="activeTab">
        <VWindowItem value="launches">
          <VAlert
            v-if="loadFailed"
            type="error"
            variant="tonal"
            class="ma-4"
          >
            <div class="d-flex align-center justify-space-between flex-wrap gap-3">
              <span>{{ t('externalLaunch.manage.messages.load_error') }}</span>
              <VBtn
                size="small"
                variant="tonal"
                color="error"
                @click="fetchLaunches"
              >
                {{ t('retry') }}
              </VBtn>
            </div>
          </VAlert>

          <VDataTableServer
            v-else
            v-model:page="launchesPage"
            v-model:items-per-page="launchesPerPage"
            :headers="launchHeaders"
            :items="launches"
            :items-length="launchesTotal"
            :loading="isLoadingLaunches"
            :loading-text="t('loading')"
            class="text-no-wrap"
            item-value="launchId"
            show-expand
          >
            <template #item.learner="{ item }">
              <div class="font-weight-medium">
                {{ learnerLabel(item) }}
              </div>
              <div class="text-body-2 text-medium-emphasis">
                {{ item.externalSubject || '—' }}
              </div>
            </template>

            <template #item.userEmail="{ item }">
              {{ item.userEmail || '—' }}
            </template>

            <template #item.attemptNo="{ item }">
              <div>{{ item.attemptNo }}</div>
              <div
                v-if="item.relaunches > 0"
                class="text-body-2 text-medium-emphasis"
              >
                {{ t('externalLaunch.manage.journal.relaunches', { value: item.relaunches }) }}
              </div>
            </template>

            <template #item.status="{ item }">
              <VChip
                :color="launchStatusColor(item.status)"
                size="small"
              >
                {{ t(`externalLaunch.manage.journal.launch_status.${item.status}`) }}
              </VChip>
              <!--
                Попытка не состоялась, а квота организации всё равно списана: она списывается
                при создании игры и не возвращается. Ровно это менеджеру и нужно видеть рядом.
              -->
              <VChip
                v-if="!item.attemptHeld && item.quotaCharged"
                color="warning"
                size="small"
                class="mt-1"
              >
                {{ t('externalLaunch.manage.journal.not_held_quota_charged') }}
              </VChip>
            </template>

            <template #item.game="{ item }">
              <span v-if="item.gameNumber !== null">#{{ item.gameNumber }}</span>
              <span v-else>—</span>
              <div
                v-if="item.gameStatus"
                class="text-body-2 text-medium-emphasis"
              >
                {{ item.gameStatus }}
              </div>
            </template>

            <template #item.createdAt="{ item }">
              <div>{{ moment(item.createdAt) }}</div>
              <div
                v-if="item.lastLaunchedAt && item.lastLaunchedAt !== item.createdAt"
                class="text-body-2 text-medium-emphasis"
              >
                {{ t('externalLaunch.manage.journal.last_launch', { value: moment(item.lastLaunchedAt) }) }}
              </div>
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex justify-end">
                <IconBtn
                  :disabled="item.status === 'revoked'"
                  @click="askRevoke(item)"
                >
                  <VTooltip
                    location="top"
                    activator="parent"
                  >
                    {{ t('externalLaunch.manage.journal.revoke') }}
                  </VTooltip>
                  <VIcon icon="tabler-hand-stop" />
                </IconBtn>
              </div>
            </template>

            <template #expanded-row="{ item, columns }">
              <tr class="v-data-table__tr">
                <td :colspan="columns.length">
                  <div class="py-4">
                    <div class="text-body-2 text-medium-emphasis mb-2">
                      {{ t('externalLaunch.manage.journal.learner_contacts', {
                        email: item.assertedEmail || '—',
                        ip: item.ip || '—',
                      }) }}
                    </div>
                    <div
                      v-if="item.resultDeadlineAt"
                      class="text-body-2 text-medium-emphasis mb-2"
                    >
                      {{ t('externalLaunch.manage.journal.result_deadline', { value: moment(item.resultDeadlineAt) }) }}
                    </div>

                    <div
                      v-if="!item.deliveries.length"
                      class="text-body-2 text-disabled"
                    >
                      {{ t('externalLaunch.manage.journal.no_deliveries') }}
                    </div>

                    <VTable
                      v-else
                      density="compact"
                    >
                      <thead>
                        <tr>
                          <th>{{ t('externalLaunch.manage.journal.delivery.version') }}</th>
                          <th>{{ t('externalLaunch.manage.journal.delivery.status') }}</th>
                          <th>{{ t('externalLaunch.manage.journal.delivery.outcome') }}</th>
                          <th>{{ t('externalLaunch.manage.journal.delivery.result') }}</th>
                          <th>{{ t('externalLaunch.manage.journal.delivery.score') }}</th>
                          <th>{{ t('externalLaunch.manage.journal.delivery.attempts') }}</th>
                          <th>{{ t('externalLaunch.manage.journal.delivery.last_attempt') }}</th>
                          <th>{{ t('externalLaunch.manage.journal.delivery.external_state') }}</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="delivery in item.deliveries"
                          :key="delivery.id"
                        >
                          <td>{{ delivery.resultVersion }}</td>
                          <td>
                            <VChip
                              :color="deliveryStatusColor(delivery.status)"
                              size="small"
                            >
                              {{ t(`externalLaunch.manage.journal.delivery_status.${delivery.status}`) }}
                            </VChip>
                          </td>
                          <td>
                            {{ delivery.outcome ? t(`externalLaunch.manage.journal.delivery_outcome.${delivery.outcome}`) : '—' }}
                          </td>
                          <td>{{ deliveryResultLabel(delivery) }}</td>
                          <td>{{ deliveryScoreLabel(delivery) }}</td>
                          <td>{{ delivery.attempts }}</td>
                          <td>{{ moment(delivery.lastAttemptAt) }}</td>
                          <td class="text-truncate external-state-cell">
                            <VTooltip
                              v-if="delivery.externalState || delivery.lastError"
                              location="top"
                              activator="parent"
                            >
                              {{ delivery.lastError || externalStateLabel(delivery) }}
                            </VTooltip>
                            {{ externalStateLabel(delivery) }}
                          </td>
                          <td>
                            <VBtn
                              v-if="delivery.status !== 'done'"
                              size="small"
                              variant="text"
                              @click="retryDelivery(delivery)"
                            >
                              {{ t('externalLaunch.manage.journal.retry_delivery') }}
                            </VBtn>
                          </td>
                        </tr>
                      </tbody>
                    </VTable>
                  </div>
                </td>
              </tr>
            </template>

            <template #no-data>
              <div class="text-center text-disabled py-6">
                {{ t('externalLaunch.manage.journal.empty_launches') }}
              </div>
            </template>
          </VDataTableServer>
        </VWindowItem>

        <VWindowItem value="events">
          <VDataTableServer
            v-model:page="eventsPage"
            v-model:items-per-page="eventsPerPage"
            :headers="eventHeaders"
            :items="events"
            :items-length="eventsTotal"
            :loading="isLoadingEvents"
            :loading-text="t('loading')"
            class="text-no-wrap"
          >
            <template #item.type="{ item }">
              {{ t(`externalLaunch.manage.journal.event_type.${item.type}`) }}
            </template>

            <template #item.refusalCode="{ item }">
              <!--
                Своя ветка причин, а не заголовки из `externalLaunch.refusal.*`: те обращены к
                ученику («Ваша учётная запись отключена»), и менеджер читал бы их как чужую
                реплику вместо причины отказа.
              -->
              {{ item.refusalCode ? t(`externalLaunch.manage.journal.refusal.${item.refusalCode}`) : '—' }}
            </template>

            <template #item.requestProof="{ item }">
              {{ item.requestProof ? t(`externalLaunch.manage.journal.request_proof.${item.requestProof}`) : '—' }}
            </template>

            <template #item.ip="{ item }">
              {{ item.ip || '—' }}
            </template>

            <template #item.userAgent="{ item }">
              <span class="d-inline-block text-truncate user-agent-cell">{{ item.userAgent || '—' }}</span>
            </template>

            <template #item.createdAt="{ item }">
              {{ moment(item.createdAt) }}
            </template>

            <template #no-data>
              <div class="text-center text-disabled py-6">
                {{ t('externalLaunch.manage.journal.empty_events') }}
              </div>
            </template>
          </VDataTableServer>
        </VWindowItem>
      </VWindow>
    </VCard>

    <ConfirmDialog
      v-model:is-dialog-visible="isRevokeConfirmVisible"
      :confirmation-question="t('externalLaunch.manage.journal.revoke_confirm')"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="confirmRevoke"
    />

    <VSnackbar
      v-model="isSnackbarVisible"
      location="top end"
      :color="snackbarColor"
    >
      {{ snackbarText }}
    </VSnackbar>
  </div>
</template>

<style lang="scss" scoped>
.external-state-cell {
  max-inline-size: 220px;
}

.user-agent-cell {
  max-inline-size: 280px;
}
</style>
